"use client";

import { motion } from "framer-motion";
import { ArrowRight, Clock, Tag } from "lucide-react";
import { fadeUp, Section } from "./utils";

const featuredPosts = [
  {
    title: "Multi-Agent Orchestration Best Practices for OpenAI",
    excerpt:
      "Proven patterns for coordinating multiple OpenAI-powered agents — from task routing and error recovery to shared memory and graceful handoffs.",
    category: "AI Agent Management",
    readTime: "9 min read",
    slug: "multi-agent-orchestration-best-practices-for-openai",
  },
  {
    title: "How Abu Dhabi Real Estate Transformed with Multi-Language AI Support",
    excerpt:
      "How a leading Abu Dhabi brokerage deployed multi-language AI agents to serve Arabic, English, Hindi, and Urdu-speaking clients — boosting lead conversion by 3x.",
    category: "Gulf Business Operations",
    readTime: "10 min read",
    slug: "how-abu-dhabi-real-estate-transformed-with-multi-language-ai-support",
  },
  {
    title: "Agent Memory Systems with React: A Complete Guide",
    excerpt:
      "Build a four-layer memory architecture in React that gives your AI agents persistent context across conversations, tasks, and sessions.",
    category: "AI Agent Management",
    readTime: "8 min read",
    slug: "agent-memory-systems-with-react-a-complete-guide",
  },
];

export function BlogPreview() {
  return (
    <Section id="blog" className="px-6 py-24 max-w-7xl mx-auto">
      <div className="text-center mb-16">
        <motion.p
          variants={fadeUp}
          custom={0}
          className="text-xs font-semibold uppercase tracking-widest text-coral-400 mb-4"
        >
          From the Blog
        </motion.p>
        <motion.h2
          variants={fadeUp}
          custom={1}
          className="text-3xl sm:text-4xl font-bold tracking-tight"
        >
          Insights on AI Agents & Automation
        </motion.h2>
        <motion.p
          variants={fadeUp}
          custom={2}
          className="mt-4 text-content-secondary max-w-2xl mx-auto"
        >
          Practical guides on multi-agent orchestration, workflow automation,
          and scaling operations across the Gulf region.
        </motion.p>
      </div>

      <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
        {featuredPosts.map((post, i) => (
          <motion.a
            key={post.slug}
            href={`/blog/${post.slug}`}
            variants={fadeUp}
            custom={i + 3}
            className="group block rounded-2xl border border-surface-border bg-surface-secondary/50 p-6 transition-all duration-300 hover:border-surface-hover hover:-translate-y-1 hover:shadow-xl hover:shadow-black/20"
          >
            <div className="flex items-center gap-2 mb-4">
              <span className="text-[11px] font-medium text-coral-400 bg-coral-500/10 border border-coral-500/20 rounded-full px-2.5 py-0.5">
                {post.category}
              </span>
              <span className="flex items-center gap-1 text-[11px] text-content-muted">
                <Clock size={11} />
                {post.readTime}
              </span>
            </div>
            <h3 className="text-lg font-semibold text-content mb-3 group-hover:text-content transition-colors line-clamp-2">
              {post.title}
            </h3>
            <p className="text-sm text-content-muted leading-relaxed line-clamp-3">
              {post.excerpt}
            </p>
            <div className="mt-4 flex items-center gap-1.5 text-sm text-coral-400 opacity-0 group-hover:opacity-100 transition-opacity">
              Read more <ArrowRight size={14} />
            </div>
          </motion.a>
        ))}
      </div>

      <motion.div variants={fadeUp} custom={6} className="text-center mt-12">
        <a
          href="/blog"
          className="inline-flex items-center gap-2 text-sm font-medium text-content-secondary hover:text-content border border-surface-border hover:border-surface-hover rounded-full px-6 py-3 transition-all duration-200"
        >
          View all articles
          <ArrowRight size={16} />
        </a>
      </motion.div>
    </Section>
  );
}
